import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleDollarSign, LockOpen, Plus, ShieldAlert, Store, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { api, errorMessage, post } from '../lib/api';
import { AUDIT_LABEL, formatBRL, formatDateTime, relativeTime } from '../lib/format';
import { useToast } from '../ui/feedback';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, PageHeader, Spinner } from '../ui/primitives';

type Dashboard = {
  restaurants: { total: number; active: number; onboarding: number; suspended: number; newLast30d: number };
  staffCount: number;
  garlicMonthlyRecurringCents: number;
  restaurantVolume: { available: boolean; note: string; confirmed30dCents?: number; confirmed30dCount?: number; pendingDigitalCents?: number; pendingDigitalCount?: number };
  security: {
    failedLogins24h: number;
    lockedAccounts: { restaurantId: string; restaurantName: string; slug: string | null; email: string; userId: string | null; name: string | null; until: string | null; failures: number }[];
  };
  services: { key: string; label: string; status: 'ok' | 'error' | 'not_configured' | 'not_available' }[];
  recentEvents: { id: string; action: string; actorLabel: string | null; createdAt: string; restaurant: { name: string; slug: string } | null }[];
};

const serviceBadge = {
  ok: <Badge tone="success" dot>Operando</Badge>,
  error: <Badge tone="danger" dot>Com falha</Badge>,
  not_configured: <Badge tone="warning" dot>Não configurado</Badge>,
  not_available: <Badge dot>Fase futura</Badge>,
};

function Stat({ icon, label, value, sub }: { icon: ReactNode; label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-sm text-ink-3">
        {icon}
        {label}
      </div>
      <div className="mt-2 font-display text-3xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-ink-3">{sub}</div>}
    </Card>
  );
}

export function DevDashboard() {
  const q = useQuery({ queryKey: ['dev-dashboard'], queryFn: () => api<Dashboard>('/api/dev/dashboard'), refetchInterval: 60_000 });
  return (
    <>
      <PageHeader
        title="Visão geral"
        description="Saúde da plataforma Garlic."
        action={
          <Link to="/dev/restaurantes/novo">
            <Button icon={<Plus className="size-4" />}>Novo restaurante</Button>
          </Link>
        }
      />
      {q.isPending && <Spinner />}
      {q.isError && <Alert tone="danger">Não foi possível carregar os indicadores.</Alert>}
      {q.data && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Stat
              icon={<Store className="size-4" />}
              label="Restaurantes"
              value={q.data.restaurants.total}
              sub={`${q.data.restaurants.active} ativos · ${q.data.restaurants.onboarding} em implantação · ${q.data.restaurants.suspended} suspensos`}
            />
            <Stat icon={<Users className="size-4" />} label="Funcionários ativos" value={q.data.staffCount} sub={`${q.data.restaurants.newLast30d} restaurantes novos em 30 dias`} />
            <Stat
              icon={<CircleDollarSign className="size-4" />}
              label="Receita recorrente do Garlic"
              value={formatBRL(q.data.garlicMonthlyRecurringCents)}
              sub="Mensalidades de restaurantes ativos. Não inclui vendas dos restaurantes."
            />
            <Stat
              icon={<ShieldAlert className="size-4" />}
              label="Logins recusados (24 h)"
              value={q.data.security.failedLogins24h}
              sub="Painel DEV e restaurantes"
            />
          </div>

          {q.data.security.lockedAccounts.length > 0 && <LockedLogins items={q.data.security.lockedAccounts} />}

          <Alert tone="info" title={`Volume transacionado pelos restaurantes: ${formatBRL(q.data.restaurantVolume.confirmed30dCents ?? 0)} (30 dias)`}>
            {q.data.restaurantVolume.note}. {q.data.restaurantVolume.confirmed30dCount ?? 0} pagamento(s) confirmados; {formatBRL(q.data.restaurantVolume.pendingDigitalCents ?? 0)} em {q.data.restaurantVolume.pendingDigitalCount ?? 0} pagamento(s) digitais pendentes.
            Esse dinheiro é dos estabelecimentos — o Garlic não recebe nem retém — e nunca entra na receita do Garlic.
          </Alert>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.4fr]">
            <Card>
              <CardHeader title="Serviços e integrações" />
              <ul className="divide-y divide-line">
                {q.data.services.map((s) => (
                  <li key={s.key} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                    <span>{s.label}</span>
                    {serviceBadge[s.status]}
                  </li>
                ))}
              </ul>
            </Card>
            <Card>
              <CardHeader title="Eventos recentes" action={<Link to="/dev/auditoria" className="text-sm font-medium text-garlic-600 hover:underline">Ver tudo</Link>} />
              {q.data.recentEvents.length === 0 ? (
                <EmptyState title="Nenhum evento ainda" />
              ) : (
                <ul className="divide-y divide-line">
                  {q.data.recentEvents.map((e) => (
                    <li key={e.id} className="flex items-start justify-between gap-3 px-5 py-3 text-sm">
                      <div className="min-w-0">
                        <p className="font-medium">{AUDIT_LABEL[e.action] ?? e.action}</p>
                        <p className="truncate text-xs text-ink-3">
                          {e.restaurant ? `${e.restaurant.name} · ` : ''}
                          {e.actorLabel ?? 'sistema'}
                        </p>
                      </div>
                      <time className="shrink-0 text-xs text-ink-3" dateTime={e.createdAt}>
                        {relativeTime(e.createdAt)}
                      </time>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      )}
    </>
  );
}

/** Funcionários com login travado por tentativas erradas, em qualquer restaurante. */
function LockedLogins({ items }: { items: Dashboard['security']['lockedAccounts'] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<string>();
  const unlock = async (restaurantId: string, userId: string) => {
    setBusy(userId);
    try {
      await post(`/api/dev/restaurants/${restaurantId}/members/${userId}/unlock`);
      toast.success('Login liberado. A pessoa já pode tentar de novo.');
      await qc.invalidateQueries({ queryKey: ['dev-dashboard'] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <Card className="border-warn-600/40">
      <CardHeader title="Logins travados agora" description="5 senhas erradas em 15 minutos travam a conta. Libere quando tiver certeza de que é a própria pessoa (ex.: confirmou por telefone)." />
      <ul className="divide-y divide-line">
        {items.map((l) => (
          <li key={`${l.restaurantId}-${l.email}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
            <div className="min-w-0">
              <p className="font-medium">{l.name ?? l.email}</p>
              <p className="truncate text-xs text-ink-3">
                {l.restaurantName} · {l.email} · {l.failures} tentativa(s){l.until ? ` · libera sozinho às ${formatDateTime(l.until)}` : ''}
              </p>
            </div>
            {l.userId ? (
              <Button variant="secondary" size="sm" loading={busy === l.userId} icon={<LockOpen className="size-3.5" />} onClick={() => unlock(l.restaurantId, l.userId!)}>Desbloquear</Button>
            ) : (
              <span title="Este e-mail não é de ninguém da equipe: pode ser tentativa de invasão ou erro de digitação."><Badge tone="warning">E-mail fora da equipe</Badge></span>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
